<template>
  <div>
    <!--
      「書き直す」は署名欄の上に置く（下だと、その下にある保存などのボタンと押し間違える）
      label スロット：項目名を同じ行の左に出す（親が渡す）
    -->
    <div v-if="!readonly" class="flex items-center mb-1">
      <slot name="label" />
      <WhiteButton text="書き直す" :size="clearButtonSize" class="ml-auto" @click="clear" />
    </div>

    <div
      class="relative border rounded bg-white"
      :class="invalid ? 'border-red-500' : 'border-gray-300'"
      :style="{ height: height + 'px' }"
    >
      <canvas
        ref="canvas"
        class="w-full h-full block signature-canvas"
        @mousedown="start"
        @mousemove="move"
        @mouseup="end"
        @mouseleave="end"
        @touchstart.prevent="start"
        @touchmove.prevent="move"
        @touchend.prevent="end"
        @touchcancel.prevent="end"
      ></canvas>
      <!-- 未署名の場合のみ案内を表示する -->
      <div
        v-if="empty"
        class="absolute inset-0 flex items-end justify-end p-2 text-xs text-gray-400 pointer-events-none"
      >
        {{ placeholder }}
      </div>
    </div>
  </div>
</template>

<script>
import WhiteButton from '@components/WhiteButton.vue'

export default {
  name: 'SignaturePad',

  components: {
    WhiteButton
  },

  props: {
    // 署名欄の高さ（px）
    height: {
      type: Number,
      default: 160
    },
    // 線の太さ
    lineWidth: {
      type: Number,
      default: 3
    },
    // 未署名時に表示する案内
    placeholder: {
      type: String,
      default: '指またはタッチペンで署名'
    },
    // 入力不可
    readonly: {
      type: Boolean,
      default: false
    },
    // 未入力を赤枠で知らせる（必須のとき親が渡す）
    invalid: {
      type: Boolean,
      default: false
    },
    // 「書き直す」ボタンの大きさ（WhiteButton の size。スマホは指で押せるよう大きくする）
    clearButtonSize: {
      type: String,
      default: 'xs'
    }
  },

  data() {
    return {
      // 描画コンテキスト
      ctx: null,
      // 描画中
      drawing: false,
      // 未署名
      empty: true,
      // 表示倍率（端末の解像度に合わせて補正する）
      ratio: 1,
      // リサイズ時に退避した署名（復元が終わるまで保持する）
      pendingImage: null
    }
  },

  mounted() {
    this.setup()
    window.addEventListener('resize', this.setup)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.setup)
  },

  methods: {
    /**
     * キャンバスの初期化
     * ＊表示サイズと解像度を合わせないと線がぼやけるため、倍率をかけている
     */
    setup() {
      const canvas = this.$refs.canvas
      // ダイアログの中ではdisplay:noneの状態でmountedが走るため、表示サイズが取れるまで初期化しない
      if (!canvas || !canvas.offsetWidth || !canvas.offsetHeight) {
        return
      }
      // 描いている最中にキャンバスを作り直すと線が途切れるため、何もしない
      if (this.drawing) {
        return
      }

      // 描いた内容を退避（リサイズで消えないようにする）
      // ＊復元前にもう一度リサイズされると空のキャンバスを退避してしまうため、
      //   復元が終わるまでは前回退避したものを使う
      const saved = this.pendingImage || (this.empty ? null : canvas.toDataURL('image/png'))

      // 画面の拡大率が1.25倍などの場合に小数になるため、ensureSizeの判定と揃えて丸めておく
      this.ratio = window.devicePixelRatio || 1
      canvas.width = Math.round(canvas.offsetWidth * this.ratio)
      canvas.height = Math.round(canvas.offsetHeight * this.ratio)

      const ctx = canvas.getContext('2d')
      ctx.scale(this.ratio, this.ratio)
      ctx.lineWidth = this.lineWidth
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.strokeStyle = '#000000'
      this.ctx = ctx

      if (saved) {
        this.pendingImage = saved
        const img = new Image()
        img.onload = () => {
          ctx.drawImage(img, 0, 0, canvas.offsetWidth, canvas.offsetHeight)
          this.pendingImage = null
        }
        img.src = saved
      }
    },

    /**
     * 表示サイズとキャンバスの解像度がずれていないか確認する
     * ＊ダイアログの中では非表示のまま mounted が走るため、表示された時点で取り直す
     */
    ensureSize() {
      const canvas = this.$refs.canvas
      if (!canvas || !canvas.offsetWidth || !canvas.offsetHeight) {
        return
      }
      const ratio = window.devicePixelRatio || 1
      if (canvas.width !== Math.round(canvas.offsetWidth * ratio) ||
        canvas.height !== Math.round(canvas.offsetHeight * ratio)) {
        this.setup()
      }
    },

    /**
     * キャンバス上の座標を取得
     * @param event マウス・タッチイベント
     */
    getPosition(event) {
      const rect = this.$refs.canvas.getBoundingClientRect()
      const point = event.touches && event.touches.length ? event.touches[0] : event
      return {
        x: point.clientX - rect.left,
        y: point.clientY - rect.top
      }
    },

    /**
     * 描画開始
     */
    start(event) {
      if (this.readonly) {
        return
      }
      // 開いた直後はキャンバスのサイズが0のままのため、ここで取り直す
      this.ensureSize()
      if (!this.ctx) {
        return
      }
      this.drawing = true
      const pos = this.getPosition(event)
      this.ctx.beginPath()
      this.ctx.moveTo(pos.x, pos.y)
    },

    /**
     * 描画
     */
    move(event) {
      if (!this.drawing) {
        return
      }
      const pos = this.getPosition(event)
      this.ctx.lineTo(pos.x, pos.y)
      this.ctx.stroke()

      if (this.empty) {
        this.empty = false
        this.$emit('change', false)
      }
    },

    /**
     * 描画終了
     */
    end() {
      if (!this.drawing) {
        return
      }
      this.drawing = false
      this.ctx.closePath()
    },

    /**
     * 書き直す
     */
    clear() {
      const canvas = this.$refs.canvas
      if (canvas && this.ctx) {
        this.ctx.clearRect(0, 0, canvas.width, canvas.height)
      }
      this.pendingImage = null
      this.empty = true
      this.$emit('change', true)
    },

    /**
     * 署名を画像（data URL）で取得
     * @returns data URL（未署名の場合は空文字）
     */
    toDataUrl() {
      if (this.empty) {
        return ''
      }
      // 復元が終わっていない場合は、キャンバスが空のため退避した方を返す
      if (this.pendingImage) {
        return this.pendingImage
      }
      return this.$refs.canvas.toDataURL('image/png')
    },

    /**
     * 未署名かどうか
     */
    isEmpty() {
      return this.empty
    }
  }
}
</script>

<style scoped>
/* 署名中に画面がスクロールしないようにする */
.signature-canvas {
  touch-action: none;
}
</style>
